import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';

import { RestService } from '../../../core/services/rest.service';
import {
    EnedisAccessRightModel,
    EnedisConsumptionStep,
    EnedisConsumptionTypeSerie,
    EnedisSearchPointModel,
    SearchpointCritereModel,
} from './enedis.models';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class EnedisService {
    constructor(@Inject(RestService) private restService: RestService) {}

    public async getEnedisAccessRights(): Promise<EnedisAccessRightModel[]> {
        const path = '/enedis/access_rights';
        const users = await this.restService.get(path, {});
        return users;
    }

    public async getEnedisAccessRight(accessRightId: string): Promise<EnedisAccessRightModel> {
        const path = `/enedis/access_right/${accessRightId}`;
        return await this.restService.get(path);
    }

    public async postNewFileProof(accessRightId: string, fileProof: File) {
        const path = `/enedis/access_right/${accessRightId}/proof`;
        const formData = new FormData();
        formData.append('file', fileProof);

        return await this.restService.post(path, {}, formData);
    }

    public async createEnedisAccessRight(
        accessRight: Partial<EnedisAccessRightModel>,
        fileProof?: File | null,
    ): Promise<EnedisAccessRightModel> {
        const path = `/enedis/access_right`;
        const formData = new FormData();

        // Ajouter chaque champ de accessRight à la main
        for (const [key, value] of Object.entries(accessRight)) {
            if (value !== undefined && value !== null) {
                formData.append(
                    key,
                    value instanceof Date ? value.toISOString() : value.toString(),
                );
            }
        }

        // Ajouter le fichier seulement s’il est présent
        if (fileProof) {
            formData.append('file', fileProof);
        }

        return await this.restService.post(path, {}, formData);
    }

    public async deleteEnedisAccessRight(accessRightId: string): Promise<EnedisAccessRightModel> {
        const path = `/enedis/access_right/${accessRightId}`;
        return await this.restService.delete(path);
    }

    public async retryEnedisAccessRight(accessRightId: string): Promise<EnedisAccessRightModel> {
        const path = `/enedis/access_right/${accessRightId}/reactivate`;
        return await this.restService.post(path);
    }

    public async searchPoints(
        critere: Partial<SearchpointCritereModel>,
    ): Promise<EnedisSearchPointModel[]> {
        const path = `/enedis/searchPoint`;
        const result: EnedisSearchPointModel[] = await this.restService.post(path, {}, critere);
        return result;
    }

    public async getTechnicalData(acessRightId: string, forceReload: boolean = false) {
        let path = `/enedis/access_right/${acessRightId}/technical-data`;
        if (forceReload) {
            path += '?forceReload=true';
        }
        return await this.restService.get(path);
    }

    public async getConsumptions(
        acessRightId: string,
        dateStart: Date,
        dateEnd: Date,
        typeSerie: EnedisConsumptionTypeSerie,
        step: EnedisConsumptionStep,
        forceReload: boolean = false,
    ) {
        const path = `/enedis/access_right/${acessRightId}/consumptions`;
        const queryParams: any = {
            dateStart,
            dateEnd,
            typeSerie,
            step,
        };
        if (forceReload) {
            queryParams.forceReload = forceReload;
        }
        return await this.restService.get(path, queryParams);
    }

    public async getPowerCurve(
        acessRightId: string,
        dateStart: Date,
        dateEnd: Date,
        measuresType: string,
        step: string,
        forceReload: boolean = false,
    ) {
        const path = `/enedis/access_right/${acessRightId}/power-curve`;
        const queryParams: any = {
            dateStart,
            dateEnd,
            measuresType,
            step,
        };

        if (forceReload) {
            queryParams.forceReload = forceReload;
        }
        return await this.restService.get(path, queryParams);
    }

    public getFileProof(
        accessRightId: string,
        type: 'userToThirdParty' | 'userToConsumer',
    ): Observable<HttpResponse<Blob>> {
        const path = `/enedis/access_right/${accessRightId}/proof?type=${type}`;
        return this.restService.download(path, {});
    }
}
